"use client";

import { useState } from "react";
import Image from "next/image";
import type { CSSProperties } from "react";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Button } from "@/src/components/ui/Button";
import { Heading, Text } from "@/src/components/ui/Typography";
import { cn } from "@/src/lib/utils";

import { aiTools } from "./data";
import type { OrbitPosition } from "./AiTooling.types";

// The orbit is a square that scales fluidly between mobile and desktop via
// clamp(); every ring and chip position below is expressed as a percentage
// of that container, so they all scale together automatically — no
// separate mobile layout needed.
const ORBIT_SIZE = "clamp(17rem, 68vw, 22rem)";

// Ring 2/3/4 sizes, all as % of ring 1's diameter (22rem desktop
// reference), so they stay proportionally correct at any container size.
const RING_2_PERCENT = (15 / 22) * 100;
const RING_3_PERCENT = (8 / 22) * 100;
const RING_4_PERCENT = (4 / 22) * 100;

// Chips sit on ring 1's circumference (the outermost ring) at the four
// compass points — offset by half of ring 1's own diameter, i.e. 50%. That
// puts every chip's center exactly on the container's edge, so half of
// each chip's own width/height (fixed px from its icon+text+padding, not
// tied to the orbit's own responsive size) overflows past it. On desktop
// there's enough surrounding gutter for that to be harmless, but on a
// narrow viewport the left/right chips' overflow can reach past the
// screen edge — worse for "Internal QA agents", the longest label. --chip-
// offset (defined in animations.css) pulls the offset in a bit below the
// md: breakpoint so the overflow has room, while staying at the literal
// ring-1 edge from md: up.
const CHIP_POSITION_STYLE: Record<OrbitPosition, CSSProperties> = {
  top: { top: "calc(50% - var(--chip-offset))", left: "50%" },
  right: { top: "50%", left: "calc(50% + var(--chip-offset))" },
  bottom: { top: "calc(50% + var(--chip-offset))", left: "50%" },
  left: { top: "50%", left: "calc(50% - var(--chip-offset))" },
};

// Each tooltip opens toward the circle's center rather than outward, so it
// always has room within the container regardless of which chip is
// hovered (a chip near the container's edge can't open a tooltip further
// outward without overflowing). The left/right chips additionally sit at
// the circle's exact vertical center, same as the chip directly opposite
// them, so their tooltips anchor off the button's top-right/top-left
// corner (bottom-full, a small mb gap) rather than its vertical center —
// opening diagonally upward keeps the tooltip close to its own chip while
// still clearing the shared horizontal line the opposite chip sits on.
const TOOLTIP_POSITION_CLASS: Record<OrbitPosition, string> = {
  top: "top-full left-1/2 mt-3 -translate-x-1/3",
  bottom: "bottom-full left-1/2 mb-3 -translate-x-1/3",
  left: "left-full bottom-full -ml-10 mb-2",
  right: "right-full bottom-full -mr-10 mb-2",
};

// An SVG stroke gives explicit control over dash length via
// strokeDasharray — plain CSS `border-style: dashed` doesn't expose dash
// size at all (browsers pick their own, usually too fine to read once the
// ring is rotating). Dash values are in the 0-100 viewBox's own units, so
// a smaller ring (fewer viewBox units per rendered pixel) naturally shows
// proportionally smaller dashes than a larger one at the same values.
function OrbitRing({
  sizePercent,
  direction,
}: {
  sizePercent: number;
  direction: "clockwise" | "counterclockwise";
}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 100"
      className={cn(
        "orbit-ring absolute inset-0 m-auto",
        direction === "clockwise"
          ? "orbit-ring--clockwise"
          : "orbit-ring--counterclockwise",
      )}
      style={{ width: `${sizePercent}%`, height: `${sizePercent}%` }}
    >
      <circle
        cx="50"
        cy="50"
        r="49"
        fill="none"
        stroke="var(--color-neutral-500, #88898B)"
        strokeWidth="0.3"
        strokeDasharray="6 5"
      />
    </svg>
  );
}

export function AiTooling() {
  const [activeTool, setActiveTool] = useState<string | null>(null);

  return (
    <Section className="bg-neutral-100">
      <Container>
        <Heading as="h2" size="displayLG" className="max-w-150">
          How TechEniac Uses AI in Our Own Development
        </Heading>

        <Text size="bodyLG" className="mt-4 max-w-150 text-neutral-600">
          Our engineers use Claude, Cursor, and AI-assisted tools to ship
          faster without sacrificing quality. A 55-person team producing at
          the speed of a larger one.
        </Text>

        <div
          className="chip-orbit relative mx-auto mt-16"
          style={{ width: ORBIT_SIZE, aspectRatio: "1" }}
        >
          <OrbitRing sizePercent={100} direction="clockwise" />
          <OrbitRing sizePercent={RING_2_PERCENT} direction="counterclockwise" />
          <OrbitRing sizePercent={RING_3_PERCENT} direction="clockwise" />
          <OrbitRing sizePercent={RING_4_PERCENT} direction="counterclockwise" />

          {aiTools.map((tool, index) => (
            <div
              key={tool.name}
              className={cn(
                "absolute -translate-x-1/2 -translate-y-1/2",
                // The active chip's tooltip must paint above every other
                // chip, not just its own button — since all chips share the
                // same z-index by default, DOM order alone decides which
                // sibling wins, so a later chip can cover an earlier one's
                // open tooltip regardless of which is actually hovered.
                activeTool === tool.name ? "z-30" : "z-10",
              )}
              style={CHIP_POSITION_STYLE[tool.position]}
            >
              <Button
                type="button"
                unstyled
                className="chip-float relative flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 shadow-[0_2px_8px_rgba(32,34,35,0.06)]"
                style={{ ["--float-delay" as string]: `${index * 0.3}s` }}
                onMouseEnter={() => setActiveTool(tool.name)}
                onMouseLeave={() => setActiveTool(null)}
                onFocus={() => setActiveTool(tool.name)}
                onBlur={() => setActiveTool(null)}
              >
                <Image
                  src={tool.logoSrc}
                  alt=""
                  width={20}
                  height={20}
                  className="flex-none"
                />

                <Text
                  as="span"
                  size="labelLG"
                  className="min-w-0 max-w-24 text-neutral-700 md:max-w-none"
                >
                  {tool.name}
                </Text>

                <div
                  role="tooltip"
                  className={cn(
                    "pointer-events-none absolute z-20 w-56 rounded-sm border border-neutral-300 bg-white p-4 text-left shadow-[0_18px_40px_rgba(32,34,35,0.12)] transition-opacity duration-300",
                    TOOLTIP_POSITION_CLASS[tool.position],
                    activeTool === tool.name ? "opacity-100" : "opacity-0",
                  )}
                >
                  <Text size="bodySM" className="text-neutral-600">
                    {tool.role}
                  </Text>
                </div>
              </Button>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
